/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useLocation } from 'react-router-dom';
import { MenuItem } from '@pwc/pwc-deals-ui';
import FederatedMFE, { LoaderType } from 'common/components/FederatedMFE';
import { injectReducerAsync } from 'app/reduxStore';
import { USER_ROLES } from '@pwc/pwc-deals-web-common';
import {
  getUserRole,
  getFakeClientUser,
  getIsThirdPartyMember,
} from 'common/redux/security/loggedInUser/loggedInUserSlice';
import Icon from '@pwc/pwc-deals-ui/build/common/Icon';
import {
  projectOverviewPath,
  projectSettingsPath,
  getUserManagementPath,
} from 'common/services/urlService';
import {
  setShowProjectList,
  showProjectListMenu,
} from 'features/appSettings/appSettingsSlice';
import AddProjectItemsNavMenu from './AddProjectItemsNavMenu';
import ShowForPermissions from 'features/security/ShowForPermissions';
import './ProjectSideNavButtons.scss';
import { getProject, getProjectSource } from 'features/project/projectSlice';
import { ProjectItemsNavMenuPopover } from './ProjectItemsNavMenuPopover';
import ProjectListSideNavButtons from './ProjectListSideNavButtons';
import {
  getPagedProjects,
  getPagedProjectsSelector,
} from 'features/projects/pagedProjectsSlice';
import { isUMEnabled } from 'features/project/projectUtil';
import { Box, useTheme } from '@mui/material';
import { ControlledSideNav } from './ControlledSideNav';
import { useTranslation } from 'react-i18next';
import styles from './ProjectListSideNavButtons.module.scss';

const ICON_SIZE = 24;

const ProjectSideNavButtons = ({ closeSideNav }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id: projectId, name: projectName } = useSelector(getProject);
  const isThirdPartyMember = useSelector(getIsThirdPartyMember);
  const showProjectList = useSelector(showProjectListMenu);
  const location = useLocation();
  const [activeMenuItem, setActiveMenuItem] = useState(location.pathname);
  const { pageSize } = useSelector(getPagedProjectsSelector);
  const isPwcUser = useSelector(getUserRole) === USER_ROLES.PWC_USER;
  const isFakeClientUser = useSelector(getFakeClientUser);
  const projectSource = useSelector(getProjectSource);
  const [isOpen, setIsOpen] = useState({ visible: false, top: 0 });
  const [isNavScrollable, setIsNavScrollable] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!wrapperRef?.current) return;
    const calculateNavScroll = () =>
      setIsNavScrollable(
        wrapperRef.current.clientHeight < wrapperRef.current.scrollHeight
      );

    calculateNavScroll();

    window.addEventListener('resize', calculateNavScroll);
    return () => {
      window.removeEventListener('resize', calculateNavScroll);
    };
  }, [wrapperRef?.current]);

  useEffect(() => {
    setActiveMenuItem(location.pathname);
  }, [location]);

  const isExactActive = (linkPath) => {
    return linkPath === activeMenuItem ? 'menu-active' : '';
  };

  const isActive = (linkPath) => {
    // Need to maintain both URLs for new files folders and files list
    if (
      activeMenuItem.includes('files/list') &&
      linkPath.includes('files/folders')
    ) {
      return 'menu-active';
    }

    return activeMenuItem.includes(linkPath) ? 'menu-active' : '';
  };

  const openPopover = (top, visible) => {
    setIsOpen({ visible, top });
  };

  const handleClick = (navTarget) => {
    setActiveMenuItem(navTarget);
    closeSideNav();
  };

  const handleShowAllProjects = () => {
    if (!isFakeClientUser) {
      dispatch(getPagedProjects({ page: 1, pageSize }));
      dispatch(setShowProjectList());
    }
  };
  return (
    <>
      {showProjectList ? (
        <ProjectListSideNavButtons closeSideNav={closeSideNav} mobile />
      ) : (
        <>
          <ProjectItemsNavMenuPopover
            projectId={projectId}
            isOpen={isOpen.visible}
            top={isOpen.top}
            closePopover={() => setIsOpen({ visible: false, top: 0 })}
            isPwcUser={isPwcUser}
          />
          <div
            className={classNames('menu-navigation-wrapper', {
              [styles.menuNavigationWrapper]: isNavScrollable,
            })}
            ref={wrapperRef}
          >
            <span
              role="link"
              aria-label={`All ${process.env.REACT_APP_PLATFORM_NAME} projects`}
              className="all-projects-link"
              onClick={handleShowAllProjects}
              data-testid="all-projects-link"
            >
              <span className="menu-item-icon">
                <Icon
                  name="left-chevron"
                  className="fill icon-orange"
                  width={20}
                />
              </span>
              <div
                className="all-projects-link-title"
                data-testid="all-projects-link-title"
              >
                {t('mobileViewAll')}&nbsp;
                <span key={process.env.REACT_APP_PLATFORM_NAME}>
                  {process.env.REACT_APP_PLATFORM_NAME}
                </span>
                &nbsp;{t('mobileViewProjects')}
              </div>
            </span>
            <div className="menu-navigation">
              {!isFakeClientUser ? (
                <ShowForPermissions
                  projectId={projectId}
                  permissions={[Constants.USER_PERMISSIONS.CONTRIBUTE]}
                >
                  <AddProjectItemsNavMenu
                    projectId={projectId}
                    openPopover={openPopover}
                    isOpen={isOpen.visible}
                    isNavScrollable={isNavScrollable}
                  />
                </ShowForPermissions>
              ) : null}
              <div className="sidebar-project-name">
                <h3>{projectName}</h3>
              </div>
              <div className="menu-navigation__nav-list">
                <MenuItem
                  navTarget={projectOverviewPath(projectId, isPwcUser)}
                  icon={
                    <Icon
                      name="folder"
                      className="outline icon-grey"
                      width={`${ICON_SIZE}`}
                    />
                  }
                  title={t('projectOverview')}
                  onClick={() =>
                    handleClick(projectOverviewPath(projectId, isPwcUser))
                  }
                  linkClass={isExactActive(projectOverviewPath(projectId))}
                  showHoverLabel={!isNavScrollable}
                />
                <ControlledSideNav
                  onClick={(id, url) => handleClick(url)}
                  showHoverLabel={!isNavScrollable}
                />
                {!isThirdPartyMember && (
                  <div className="footer-menu-items">
                    <Box
                      sx={
                        isFakeClientUser
                          ? {
                              pointerEvents: 'none',
                              '& .notification-bell-menu .notification-badge-container':
                                {
                                  display: 'none',
                                },
                              '& .menu-item .menu-item-title': {
                                color: theme.palette.disabledLink.main,
                              },
                              '& a.menu-item .menu-item-icon > svg.outline path.outline':
                                {
                                  fill: theme.palette.disabledLink.main,
                                },
                            }
                          : undefined
                      }
                    >
                      <FederatedMFE
                        urlKey="REACT_APP_NOTIFICATIONS_BELL_MFE_URL"
                        scope="notificationBell"
                        componentModule="./NotificationBell"
                        injectionReducerCb={injectReducerAsync}
                        suspenseFallbackStyle="d-flex justify-content-center"
                        props={{
                          onClick: handleClick,
                          activeMenuItem,
                          projectId,
                        }}
                        loader={LoaderType.Circular}
                      />
                    </Box>
                    {isUMEnabled(projectSource, isThirdPartyMember) && (
                      <Box
                        sx={
                          isFakeClientUser
                            ? {
                                pointerEvents: 'none',
                                '& .menu-item .menu-item-title': {
                                  color: theme.palette.disabledLink.main,
                                },
                                '& a.menu-item .menu-item-icon > svg.outline path.outline':
                                  {
                                    fill: theme.palette.disabledLink.main,
                                  },
                              }
                            : undefined
                        }
                      >
                        <MenuItem
                          data-testid="team-members-menu-item"
                          navTarget={getUserManagementPath(projectId)}
                          icon={
                            <Icon
                              name="team-members"
                              className="outline icon-grey"
                              width={`${ICON_SIZE}`}
                            />
                          }
                          title={t('nav.teamMembers')}
                          onClick={() =>
                            handleClick(getUserManagementPath(projectId))
                          }
                          linkClass={`menu-item-footer ${isActive(
                            getUserManagementPath(projectId)
                          )}`}
                          showHoverLabel={!isNavScrollable}
                        />
                      </Box>
                    )}
                    <Box
                      sx={
                        isFakeClientUser
                          ? {
                              pointerEvents: 'none',
                              '& .menu-item .menu-item-title': {
                                color: theme.palette.disabledLink.main,
                              },
                              '& a.menu-item .menu-item-icon > svg.outline path.outline':
                                {
                                  fill: theme.palette.disabledLink.main,
                                },
                            }
                          : undefined
                      }
                    >
                      <MenuItem
                        data-testid="project-settings-menu-item"
                        navTarget={projectSettingsPath(projectId)}
                        icon={
                          <Icon
                            name="settings"
                            className="outline icon-grey"
                            width={`${ICON_SIZE}`}
                          />
                        }
                        title={t('nav.settings')}
                        onClick={() =>
                          handleClick(projectSettingsPath(projectId))
                        }
                        linkClass={`menu-item-footer ${isActive(
                          projectSettingsPath(projectId)
                        )}`}
                        showHoverLabel={!isNavScrollable}
                      />
                    </Box>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ProjectSideNavButtons;
